import { FC, ReactNode, useState } from 'react'
import { faChevronDown, faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { faCheck } from '@fortawesome/pro-solid-svg-icons'
import { Button } from '../../atoms/button'
import { Icon } from '../../atoms/icon'
import { Text } from '../../atoms/text'
import { Tooltip } from '../../atoms/tooltip'
import { TypographyStyles } from '../../../utils/style-helpers'

export interface TaskItemProps {
  header: string
  headerExtra?: ReactNode
  description: ReactNode
  additionalDetails?: ReactNode
  started: boolean
  completed: boolean
  skipped: boolean
  onActionClick: () => void
  /** Override the default "Start" / "Continue" CTA label */
  actionText?: string
  /** Hide the right-arrow icon next to the CTA label (e.g., for "Learn more") */
  actionHideArrow?: boolean
  /** When true, renders a non-expandable, greyed-out row with a disabled action button. */
  disabled?: boolean
}

export const TaskItem: FC<TaskItemProps> = ({
  header,
  headerExtra,
  description,
  additionalDetails,
  started,
  completed,
  skipped,
  onActionClick,
  actionText,
  actionHideArrow,
  disabled,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const completedOrSkipped = completed || skipped

  if (disabled) {
    return (
      <div className="flex items-center gap-4 self-stretch min-h-16 p-4 rounded-2xl border border-neutral-100 bg-neutral-0">
        <div className="flex flex-col justify-center items-center shrink-0 w-8 h-8 p-2 rounded-lg border border-neutral-200 bg-neutral-0" />
        <div className="flex flex-col flex-1 min-w-0">
          <Text
            variant={TypographyStyles.LABEL_MD}
            className="text-neutral-400 truncate"
          >
            {header}
          </Text>
          <Text
            variant={TypographyStyles.BODY_SM}
            className="text-neutral-400 truncate"
          >
            {description}
          </Text>
        </div>
        <Button disabled onClick={onActionClick} className="shrink-0">
          {actionText ?? 'Start'}
        </Button>
      </div>
    )
  }

  if (completedOrSkipped) {
    return (
      <div className="flex items-center gap-4 w-full min-h-16 p-4 rounded-2xl bg-neutral-25">
        <div className="flex flex-col justify-center items-center shrink-0 w-8 h-8 rounded-lg bg-green-50">
          <Icon icon={faCheck} color="text-neutral-0" size="xs" />
        </div>
        <Text
          variant={TypographyStyles.BODY_MD}
          className="text-neutral-400 line-through"
        >
          {header}
        </Text>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-start self-stretch min-h-16 p-4 rounded-2xl border border-neutral-100 bg-neutral-0 justify-center">
      <Button
        variant="ghost"
        className="flex items-center justify-between w-full text-left h-auto p-0"
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <div className="flex items-center gap-4 min-w-0 overflow-hidden">
          <div className="flex flex-col justify-center items-center shrink-0 w-8 h-8 p-2 rounded-lg border border-neutral-200 bg-neutral-0" />
          <Tooltip>
            <Tooltip.Trigger asChild>
              <Text
                variant={TypographyStyles.LABEL_MD}
                className="text-neutral-900 truncate"
              >
                {header}
              </Text>
            </Tooltip.Trigger>
            <Tooltip.Content side="top" align="start">
              <Text variant={TypographyStyles.BODY_SM}>{header}</Text>
            </Tooltip.Content>
          </Tooltip>
          {headerExtra}
        </div>
        <Icon
          icon={faChevronDown}
          color="text-neutral-600"
          className={`shrink-0 transition-transform duration-300 ease-in-out ${isExpanded ? 'rotate-180' : 'rotate-0'}`}
        />
      </Button>

      <div
        className={`grid transition-all duration-300 ease-in-out w-full ${isExpanded ? 'grid-rows-[1fr] opacity-100 mt-4' : 'grid-rows-[0fr] opacity-0'}`}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-4 w-full pl-12 sm:flex-row sm:items-center sm:gap-6">
            <div className="flex flex-col gap-2 flex-1">
              <Text variant={TypographyStyles.BODY_SM}>{description}</Text>
              {additionalDetails}
            </div>
            <div className="w-full sm:w-auto shrink-0">
              <Button className="w-full sm:w-auto" onClick={onActionClick}>
                {actionText ?? (started ? 'Continue' : 'Start')}
                {!actionHideArrow && (
                  <Icon
                    icon={faArrowRight}
                    className="ml-2"
                    color="text-neutral-0"
                  />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
